import type { ValidatedLiveTileSnapshot } from './live-tile.model';

export type { ValidatedLiveTileSnapshot } from './live-tile.model';

export type LiveTileSnapshotValidationResult =
  | { readonly ok: true; readonly value: ValidatedLiveTileSnapshot }
  | { readonly ok: false; readonly reason: 'malformed' | 'unsupported-version' };

const REQUIRED_FIELDS = [
  'schemaVersion',
  'clientCode',
  'day',
  'publishedAtUtc',
  'sequence',
  'eventCount',
  'totalTokens',
  'spendAmount',
  'spendCurrency',
  'errorCount',
] as const;

export function validateLiveTileSnapshot(value: unknown): LiveTileSnapshotValidationResult {
  if (!isRecord(value)) return malformed();
  if (value['schemaVersion'] !== undefined && value['schemaVersion'] !== '1.0') {
    return { ok: false, reason: 'unsupported-version' };
  }
  if (
    Object.keys(value).length !== REQUIRED_FIELDS.length
    || REQUIRED_FIELDS.some((field) => !Object.hasOwn(value, field))
  ) {
    return malformed();
  }

  const clientCode = value['clientCode'];
  const day = value['day'];
  const publishedAtUtc = value['publishedAtUtc'];
  const sequence = value['sequence'];
  const eventCount = value['eventCount'];
  const totalTokens = value['totalTokens'];
  const spendAmount = value['spendAmount'];
  const spendCurrency = value['spendCurrency'];
  const errorCount = value['errorCount'];

  if (
    value['schemaVersion'] !== '1.0'
    || typeof clientCode !== 'string'
    || !/^[A-Z0-9][A-Z0-9_-]{0,63}$/.test(clientCode)
    || !isUtcDay(day)
    || !isUtcInstant(publishedAtUtc)
    || !isNonNegativeInteger(sequence)
    || !isNonNegativeInteger(eventCount)
    || typeof totalTokens !== 'number'
    || !Number.isInteger(totalTokens)
    || typeof spendAmount !== 'number'
    || !Number.isFinite(spendAmount)
    || !(spendCurrency === null || (typeof spendCurrency === 'string' && /^[A-Z]{3}$/.test(spendCurrency)))
    || !isNonNegativeInteger(errorCount)
  ) {
    return malformed();
  }

  return {
    ok: true,
    value: {
      schemaVersion: '1.0',
      clientCode,
      day,
      publishedAtUtc,
      sequence,
      eventCount,
      totalTokens,
      spendAmount,
      spendCurrency,
      errorCount,
    },
  };
}

function malformed(): LiveTileSnapshotValidationResult {
  return { ok: false, reason: 'malformed' };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isUtcDay(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const instant = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(instant) && new Date(instant).toISOString().slice(0, 10) === value;
}

function isUtcInstant(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?Z$/.exec(value);
  if (!match || !isUtcDay(match[1])) return false;
  const hour = Number(match[2]);
  const minute = Number(match[3]);
  const second = Number(match[4]);
  return hour <= 23 && minute <= 59 && second <= 59 && Number.isFinite(Date.parse(value));
}